import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, Package, Check, SkipForward } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * CleaningConsumptionModal — registo de consumo de produtos/materiais na
 * finalização de uma limpeza.
 *
 * Props:
 *  - bookingId (str)
 *  - open (bool)
 *  - onClose()          called on X or skip
 *  - onSubmitted()      called after successful consumption POST
 *  - onSkip()           called when user clicks "Finalizar sem registo"
 */
function CleaningConsumptionModal({ bookingId, open, onClose, onSubmitted, onSkip }) {
  const [catalog, setCatalog] = useState([]);   // list of produtos + materiais
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState([newLine()]);
  const [saving, setSaving] = useState(false);
  const [alreadyLogged, setAlreadyLogged] = useState([]);

  function newLine() {
    return {
      id: `tmp-${Date.now()}-${Math.random()}`,
      product_id: "",
      quantity: 1,
      note: "",
    };
  }

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        // Full catalog (admin + employee accessible) — not filtered by month
        // so employees can register consumption even on products registered
        // in previous months.
        const { data } = await api.get("/catalog/consumable-products");
        const all = [...(data.produtos || []), ...(data.materiais || [])];
        setCatalog(all);

        // Any prior consumption registered for this booking?
        try {
          const { data: prior } = await api.get(`/bookings/${bookingId}/consumption`);
          setAlreadyLogged(prior.items || []);
        } catch { /* not critical */ }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [open, bookingId]);

  function patch(id, p) { setLines(rs => rs.map(r => r.id === id ? { ...r, ...p } : r)); }
  function remove(id) { setLines(rs => rs.length <= 1 ? rs : rs.filter(r => r.id !== id)); }

  const valid = useMemo(
    () => lines.some(l => l.product_id && Number(l.quantity) > 0),
    [lines],
  );

  // Live cost preview — sum of (quantity × unit_cost) for each valid line.
  // `unit_cost` is the price per BASE unit (ml, L, un…) incl. VAT, computed
  // server-side as unit_price ÷ pack_size × (1 + vat/100).
  const previewTotal = useMemo(() => {
    let sum = 0;
    for (const l of lines) {
      const qty = Number(l.quantity);
      if (!l.product_id || !(qty > 0)) continue;
      const prod = catalog.find(p => p.id === l.product_id);
      if (!prod) continue;
      sum += qty * (Number(prod.unit_cost) || 0);
    }
    return Math.round(sum * 100) / 100;
  }, [lines, catalog]);

  async function submit() {
    const clean = lines.filter(l => l.product_id && Number(l.quantity) > 0);
    if (!clean.length) {
      toast.error("Adicione pelo menos um produto usado.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/bookings/${bookingId}/consumption`, {
        lines: clean.map(l => ({
          product_id: l.product_id,
          quantity: Number(l.quantity),
          note: l.note.trim() || undefined,
        })),
      });
      toast.success(`${clean.length} produto(s) registado(s). Stock atualizado.`);
      onSubmitted && onSubmitted();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="cleaning-consumption-modal">
      <div className="lux-card w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <header className="flex items-center justify-between p-4 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <Package size={12} /> Consumo da limpeza
            </div>
            <div className="font-display text-xl">Que produtos foram usados?</div>
            <p className="text-xs text-[#a0b2af] mt-1 max-w-md">
              Cada produto registado aqui é descontado do stock automaticamente
              e aparece no histórico de movimentos. Pode saltar este passo e
              registar depois.
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="cleaning-consumption-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="text-center py-10"><Loader2 size={20} className="mx-auto animate-spin text-[#d4af37]" /></div>
          ) : (
            <>
              {alreadyLogged.length > 0 && (
                <div className="lux-card p-3 border-[#d4af37]/40 bg-[#d4af37]/5"
                     data-testid="cleaning-consumption-already">
                  <div className="eyebrow text-[10px] text-[#d4af37] mb-1">
                    Já registado anteriormente
                  </div>
                  <ul className="text-xs text-[#a0b2af] space-y-0.5">
                    {alreadyLogged.map(e => (
                      <li key={e.id}>• {e.product_name}: <span className="tabular-nums">{e.quantity}</span> {e.unit_label}</li>
                    ))}
                  </ul>
                  <div className="text-[10px] text-[#a0b2af] mt-2 italic">
                    As linhas abaixo adicionam-se ao registo — duplicam se repetir os mesmos produtos.
                  </div>
                </div>
              )}

              {catalog.length === 0 ? (
                <div className="lux-card p-6 text-center text-sm text-[#a0b2af]">
                  Ainda não há produtos ou materiais registados este mês.
                  Peça ao admin para registar o catálogo antes de consumir.
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {lines.map((l, idx) => (
                      <ConsumptionLineRow
                        key={l.id}
                        line={l} index={idx + 1}
                        catalog={catalog}
                        onChange={p => patch(l.id, p)}
                        onRemove={() => remove(l.id)}
                        canRemove={lines.length > 1} />
                    ))}
                    <button onClick={() => setLines(rs => [...rs, newLine()])}
                            className="btn-ghost-gold w-full py-2 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
                            data-testid="cleaning-consumption-add-line">
                      <Plus size={14} /> Adicionar outro produto
                    </button>
                  </div>

                  {previewTotal > 0 && (
                    <div className="lux-card p-3 border-[#d4af37]/40 bg-[#d4af37]/5 flex items-baseline justify-between"
                         data-testid="cleaning-consumption-preview">
                      <div>
                        <div className="eyebrow text-[10px] text-[#d4af37] mb-0.5">
                          Custo estimado desta limpeza
                        </div>
                        <div className="text-[10px] text-[#a0b2af]">
                          Calculado automaticamente com base nos preços do catálogo (c/ IVA).
                        </div>
                      </div>
                      <div className="font-display text-2xl text-[#d4af37] tabular-nums"
                           data-testid="cleaning-consumption-preview-total">
                        € {previewTotal.toFixed(2)}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#1a4640]">
                <button onClick={onSkip}
                        disabled={saving}
                        className="px-4 py-2 border border-[#1a4640] hover:border-[#a0b2af] rounded-sm text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#fdfbf7] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="cleaning-consumption-skip">
                  <SkipForward size={14} /> Saltar e finalizar
                </button>
                <button onClick={submit}
                        disabled={!valid || saving || catalog.length === 0}
                        className="btn-gold px-6 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="cleaning-consumption-submit">
                  {saving ? <><Loader2 size={14} className="animate-spin" /> A registar…</>
                          : <><Check size={14} /> Registar consumo</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConsumptionLineRow({ line, index, catalog, onChange, onRemove, canRemove }) {
  const picked = catalog.find(p => p.id === line.product_id);
  const qtyNum = Number(line.quantity) || 0;
  const lineCost = picked && qtyNum > 0
    ? Math.round(qtyNum * (Number(picked.unit_cost) || 0) * 100) / 100
    : 0;
  const unitLabel = picked?.unit_label || "un";
  return (
    <div className="lux-card p-3 space-y-2"
         data-testid={`cleaning-consumption-line-${index}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
          Produto {index}
          {picked && (
            <span className="ml-2 text-[#10b981]">
              · stock atual: {Number(picked.stock_quantity || 0)} {unitLabel}
            </span>
          )}
          {picked && Number(picked.unit_cost) > 0 && (
            <span className="ml-2 text-[#d4af37]">
              · €{Number(picked.unit_cost).toFixed(4)}/{unitLabel}
            </span>
          )}
        </div>
        {canRemove && (
          <button onClick={onRemove}
                  className="p-1 text-[#a0b2af] hover:text-[#ef4444]"
                  data-testid={`cleaning-consumption-remove-${index}`}>
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr] gap-2">
        <select value={line.product_id}
                onChange={e => onChange({ product_id: e.target.value })}
                className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
                data-testid={`cleaning-consumption-select-${index}`}>
          <option value="">— Selecionar produto —</option>
          {catalog.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} {p.code ? `(${p.code})` : ""}
              {p.category === "material" ? " · material" : ""}
            </option>
          ))}
        </select>

        <div className="relative">
          <input type="number" step="0.01" min="0.01"
                 value={line.quantity}
                 onChange={e => onChange({ quantity: e.target.value })}
                 placeholder="Qtd"
                 className="bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm pl-2 pr-10 py-1.5 text-sm text-right outline-none w-full"
                 data-testid={`cleaning-consumption-qty-${index}`} />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#a0b2af] uppercase tracking-widest pointer-events-none">
            {unitLabel}
          </span>
        </div>

        <input type="text" maxLength={120}
               value={line.note}
               onChange={e => onChange({ note: e.target.value })}
               placeholder="Nota (opcional)"
               className="bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
               data-testid={`cleaning-consumption-note-${index}`} />
      </div>

      {lineCost > 0 && (
        <div className="flex items-center justify-end text-[11px] text-[#d4af37] tabular-nums"
             data-testid={`cleaning-consumption-line-cost-${index}`}>
          Custo: € {lineCost.toFixed(2)}
        </div>
      )}
    </div>
  );
}

export default CleaningConsumptionModal;
